import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  Link,
  useTheme,
  FormHelperText,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
// import { toast } from "../../common/AppToast";
import { filledUploadDropzoneSx } from "./listNewAssetFilledSurface";

const MAX_FILE_MB = 10;

const labelSx = { fontWeight: 600, fontSize: 14, color: "text.primary", mb: 0.75 };

function RequiredLabel({ children }) {
  return (
    <Typography component="span" sx={labelSx}>
      {children}{" "}
      <Box component="span" sx={{ color: "error.main" }}>
        *
      </Box>
    </Typography>
  );
}

function OptionalLabel({ children }) {
  return (
    <Typography component="span" sx={labelSx}>
      {children}
    </Typography>
  );
}

function isPdfFile(file) {
  if (file.type === "application/pdf") return true;
  return /\.pdf$/i.test(file.name);
}

export default function ListNewAssetStep6({
  draft,
  onDraftChange,
  nextStep,
  prevStep,
  wizardStep = 6,
  wizardTotalSteps = 8,
  categoryChipLabel = "Real Estate",
  isEditMode = false,
}) {
  const theme = useTheme();
  const TEAL = theme.palette.brand.cyan;
  const TEAL_HOVER = theme.palette.brand.cyanDark;
  const [errors, setErrors] = useState({});
  const ownershipFile = draft.ownershipProof;
  const legalFile = draft.legalDocuments;

  const ownershipInputRef = useRef(null);
  const legalInputRef = useRef(null);

  const handleFile = (file, setter) => {
    if (!file) return;
    if (!isPdfFile(file)) {
      setErrors((p) => ({ ...p, [setter]: "Only PDF files are allowed." }));
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setErrors((p) => ({ ...p, [setter]: `File must be ${MAX_FILE_MB}MB or smaller.` }));
      return;
    }
    if (setter === "ownershipProof") {
      onDraftChange({ ownershipProof: file });
    } else {
      onDraftChange({ legalDocuments: file });
    }
    setErrors((p) => ({ ...p, [setter]: "" }));
  };

  const handleNext = () => {
    const hasOwnership =
      ownershipFile ||
      (isEditMode && Boolean(draft._editMeta?.titleDeedUrl));
    if (!hasOwnership) {
      setErrors({ ownershipProof: "Please upload proof of ownership / title deed." });
      return;
    }
    setErrors({});
    nextStep();
  };

  return (
    <>
      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1100,
          mx: "auto",
          minHeight: "100vh",
        }}
      >

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 2 }}
        >
          <Link
            component="button"
            type="button"
            underline="none"
            onClick={prevStep}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: TEAL,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              border: "none",
              background: "none",
              p: 0,
              "&:hover": { color: TEAL_HOVER },
            }}
          >
            <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
            Back
          </Link>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              label={categoryChipLabel}
              size="small"
              sx={{
                bgcolor: (theme) => theme.palette.brand.cyanSoft,
                color: (theme) => theme.palette.brand.cyanDark,
                fontWeight: 600,
                fontSize: 12,
                height: 26,
              }}
            />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.muted" }}>
              Step {wizardStep} of {wizardTotalSteps}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
          {Array.from({ length: wizardTotalSteps }).map((_, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 999,
                bgcolor: i < wizardStep ? TEAL : theme.palette.border.light,
              }}
            />
          ))}
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3, md: 4 },
            borderRadius: "14px",
            bgcolor: "background.paper",
            boxShadow: (theme) => theme.palette.shadow.cardStrong,
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "text.primary", mb: 0.75 }}
          >
            Required Documents
          </Typography>
          <Typography variant="body2" sx={{ color: "text.muted", mb: 3 }}>
            Upload legal documentation for verification
          </Typography>

          <DocumentUploadBlock
            label={<RequiredLabel>Proof of Ownership / Title Deed</RequiredLabel>}
            helper="PDF only (Max 10MB)"
            file={ownershipFile}
            inputRef={ownershipInputRef}
            onPick={() => ownershipInputRef.current?.click()}
            onChange={(e) => handleFile(e.target.files?.[0], "ownershipProof")}
            error={errors.ownershipProof}
          />

          <Box sx={{ mb: 3 }} />

          <DocumentUploadBlock
            label={<OptionalLabel>Legal &amp; Compliance Documents</OptionalLabel>}
            helper="PDF only (Max 10MB)"
            file={legalFile}
            inputRef={legalInputRef}
            onPick={() => legalInputRef.current?.click()}
            error={errors.legalDocuments}
            onChange={(e) => handleFile(e.target.files?.[0], "legalDocuments")}
          />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 4, pt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={prevStep}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderColor: (theme) => theme.palette.border.medium,
                color: "text.neutral",
                px: 3,
                py: 1,
                "&:hover": {
                  borderColor: (theme) => theme.palette.text.faint,
                  bgcolor: "background.subtle",
                },
              }}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
              onClick={handleNext}
              sx={{
                px: 1.5,
                py: 1,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: TEAL,
                borderRadius: "12px",
                boxShadow: "none",
                "&:hover": { bgcolor: TEAL_HOVER, boxShadow: "none" },
              }}
            >
              Next Step
            </Button>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}

function DocumentUploadBlock({ label, helper, file, inputRef, onPick, onChange, error }) {
  const theme = useTheme();
  return (
    <Box>
      <Box sx={{ mb: 0.75 }}>{label}</Box>
      <Typography sx={{ fontSize: 13, color: "text.muted", mb: 1.5 }}>
        {helper}
      </Typography>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        hidden
        onChange={onChange}
      />
      <Paper
        component="button"
        type="button"
        onClick={onPick}
        elevation={0}
        sx={{
          width: "100%",
          minHeight: 160,
          borderRadius: 2,
          bgcolor: "background.subtle",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          p: 3,
          cursor: "pointer",
          "&:hover": {
            borderColor: (theme) => theme.palette.brand.cyan,
            bgcolor: "background.paper",
          },
          ...filledUploadDropzoneSx(theme, Boolean(file), theme.palette.border.light),
        }}
      >
        <Stack alignItems="center" spacing={1}>
          <ArrowUpwardIcon
            sx={{
              color: (theme) => theme.palette.brand.cyan,
              fontSize: 36,
            }}
          />
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "text.neutral" }}>
            Upload Document
          </Typography>
          {file && (
            <Typography
              sx={{
                fontSize: 13,
                color: (theme) => theme.palette.brand.cyanDark,
                fontWeight: 600,
                mt: 0.5,
              }}
            >
              {file.name}
            </Typography>
          )}
        </Stack>
      </Paper>
      {error && (
        <FormHelperText error sx={{ mt: 0.5 }}>{error}</FormHelperText>
      )}
    </Box>
  );
}
